import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Settings, Plus, Trash2, CheckCircle2, AlertCircle, XCircle,
  Loader2, Copy, ChevronRight, Server, Shield, ArrowRight, RefreshCw,
  Globe, Lock, Eye, EyeOff, ExternalLink, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESET_PROVIDERS = [
  { name: 'Gmail', imap_host: 'imap.gmail.com', imap_port: 993, smtp_host: 'smtp.gmail.com', smtp_port: 587, use_ssl: true, use_tls: true, note: 'Use an App Password (not your Gmail password). Go to Google Account > Security > 2-Step Verification > App Passwords.' },
  { name: 'Outlook / Hotmail', imap_host: 'outlook.office365.com', imap_port: 993, smtp_host: 'smtp.office365.com', smtp_port: 587, use_ssl: true, use_tls: true, note: 'Use your Outlook/Hotmail email and password.' },
  { name: 'Yahoo Mail', imap_host: 'imap.mail.yahoo.com', imap_port: 993, smtp_host: 'smtp.mail.yahoo.com', smtp_port: 465, use_ssl: true, use_tls: false, note: 'Generate an App Password in Yahoo Account Security settings.' },
  { name: 'Zoho Mail', imap_host: 'imap.zoho.com', imap_port: 993, smtp_host: 'smtp.zoho.com', smtp_port: 587, use_ssl: true, use_tls: true, note: 'Use your Zoho email and password or App Password.' },
  { name: 'Custom', imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 587, use_ssl: true, use_tls: true, note: 'Enter your IMAP/SMTP server details manually.' },
];

const EmailIntegrationPage = () => {
  // Honour `?tab=domain|gmail|accounts|forwarding` so the Super-Admin
  // sidebar (and any deep link) can land directly on the right tab
  // without forcing a manual click. Defaults to "accounts".
  const initialTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['accounts', 'gmail', 'domain', 'forwarding'].includes(t) ? t : 'accounts';
    } catch {
      return 'accounts';
    }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [accounts, setAccounts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [forwardingAddress, setForwardingAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const [formData, setFormData] = useState({
    account_name: '',
    email_address: '',
    provider_type: 'imap_smtp',
    imap_host: '',
    imap_port: 993,
    smtp_host: '',
    smtp_port: 587,
    username: '',
    password: '',
    use_ssl: true,
    use_tls: true
  });

  const [domainForm, setDomainForm] = useState({ domain: '', sending_subdomain: '', reply_to_email: '' });
  const [gmailStatus, setGmailStatus] = useState(null);
  const [connectingGmail, setConnectingGmail] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/email/accounts`, { headers });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('Failed to load email accounts', err);
    }
  }, []);

  const loadDomains = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/email/domain-auth`, { headers });
      setDomains(res.data.domains || []);
    } catch (err) {
      console.error('Failed to load domains', err);
    }
  }, []);

  const loadForwardingAddress = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/email/forwarding-address`, { headers });
      setForwardingAddress(res.data);
    } catch (err) {
      console.error('Failed to load forwarding address', err);
    }
  }, []);

  const loadGmailStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/email/oauth/gmail/status`, { headers });
      setGmailStatus(res.data);
    } catch (err) {
      console.error('Failed to load Gmail status', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadAccounts(), loadDomains(), loadForwardingAddress(), loadGmailStatus()]);
      setLoading(false);
    };
    loadAll();
    
    // Check URL params for Gmail OAuth callback
    const params = new URLSearchParams(window.location.search);
    const gmailConnected = params.get('gmail_connected');
    const gmailError = params.get('gmail_error');
    const gmailEmail = params.get('email');
    
    if (gmailConnected === 'true') {
      toast.success(`Gmail account ${gmailEmail || ''} connected successfully!`);
      setActiveTab('gmail');
      window.history.replaceState({}, '', '/integrations/email');
      loadGmailStatus();
      loadAccounts();
    } else if (gmailError) {
      toast.error(`Gmail connection failed: ${gmailError}`);
      setActiveTab('gmail');
      window.history.replaceState({}, '', '/integrations/email');
    }
  }, [loadAccounts, loadDomains, loadForwardingAddress, loadGmailStatus]);

  const selectProvider = (provider) => {
    setSelectedProvider(provider);
    setFormData(prev => ({
      ...prev,
      imap_host: provider.imap_host,
      imap_port: provider.imap_port,
      smtp_host: provider.smtp_host,
      smtp_port: provider.smtp_port,
      use_ssl: provider.use_ssl,
      use_tls: provider.use_tls,
    }));
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/email/accounts/test-connection`, {
        ...formData,
        username: formData.username || formData.email_address
      }, { headers });
      const { imap, smtp } = res.data;
      if (imap.success && smtp.success) {
        toast.success('Both IMAP and SMTP connections successful!');
      } else {
        if (!imap.success) toast.error(`IMAP: ${imap.message}`);
        if (!smtp.success) toast.error(`SMTP: ${smtp.message}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection test failed');
    }
    setTesting(false);
  };

  const handleSaveAccount = async () => {
    if (!formData.email_address || !formData.account_name) {
      toast.error('Please fill in account name and email address');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/email/accounts`, {
        ...formData,
        username: formData.username || formData.email_address
      }, { headers });
      toast.success('Email account connected successfully!');
      setAddDialogOpen(false);
      setFormData({ account_name: '', email_address: '', provider_type: 'imap_smtp', imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 587, username: '', password: '', use_ssl: true, use_tls: true });
      setSelectedProvider(null);
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save account');
    }
    setSaving(false);
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Remove this email account? Existing conversations will be preserved.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/email/accounts/${accountId}`, { headers });
      toast.success('Account removed');
      loadAccounts();
    } catch (err) {
      toast.error('Failed to remove account');
    }
  };

  const handleTestExisting = async (accountId) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/email/accounts/${accountId}/test`, {}, { headers });
      const { imap, smtp } = res.data;
      if (imap.success && smtp.success) {
        toast.success('Connection verified!');
      } else {
        toast.error(`IMAP: ${imap.message} | SMTP: ${smtp.message}`);
      }
    } catch (err) {
      toast.error('Test failed');
    }
  };

  const handleSyncAccount = async (accountId) => {
    try {
      await axios.post(`${API_BASE_URL}/email/accounts/${accountId}/sync`, {}, { headers });
      toast.success('Email sync started');
    } catch (err) {
      toast.error('Sync failed');
    }
  };

  const handleAddDomain = async () => {
    if (!domainForm.domain) {
      toast.error('Please enter a domain');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/email/domain-auth`, domainForm, { headers });
      toast.success('Domain added. Configure the DNS records shown below.');
      setDomainDialogOpen(false);
      setDomainForm({ domain: '', sending_subdomain: '', reply_to_email: '' });
      loadDomains();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Remove this domain configuration?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/email/domain-auth/${domainId}`, { headers });
      toast.success('Domain removed');
      loadDomains();
    } catch (err) {
      toast.error('Failed to remove domain');
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      toast.info('Checking DNS records...');
      const res = await axios.post(`${API_BASE_URL}/email/domain-auth/${domainId}/verify`, {}, { headers });
      const rs = res.data.record_statuses || {};
      const activeCount = Object.values(rs).flat().filter(s => s === 'active').length;
      const totalCount = Object.values(rs).flat().length;
      // Surface SPF conflict explicitly — silent merge guidance is the
      // most common deliverability footgun (multiple SPF TXT records).
      if (res.data.spf_warning) {
        toast.error(`SPF conflict: ${res.data.spf_warning.count} SPF records found on this domain. Merge them into one.`);
      } else if (res.data.status === 'verified') {
        toast.success('All records verified!');
      } else {
        toast.info(`${activeCount}/${totalCount} records active. ${res.data.status === 'partially_verified' ? 'Domain verified, some DNS records pending.' : 'Check DNS records.'}`);
      }
      loadDomains();
    } catch (err) {
      toast.error('Verification check failed');
    }
  };

  const handleConnectGmail = async () => {
    setConnectingGmail(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/email/oauth/gmail/connect`, { headers });
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Gmail connection');
      setConnectingGmail(false);
    }
  };

  const handleBackfillGmail = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/email/accounts/${accountId}/sync?days=30`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Backfill sync started — emails from the last 30 days will appear shortly');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start backfill sync');
    }
  };

  const handleDisconnectGmail = async (accountId) => {
    if (!window.confirm('Disconnect this Gmail account? Existing conversations will be preserved.')) return;
    try {
      await axios.post(`${API_BASE_URL}/email/oauth/gmail/${accountId}/disconnect`, {}, { headers });
      toast.success('Gmail account disconnected');
      loadGmailStatus();
      loadAccounts();
    } catch (err) {
      toast.error('Failed to disconnect Gmail');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="email-integration-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-playfair text-wt-text-primary flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
              <Mail className="h-7 w-7 text-white" />
            </div>
            Email Integration
          </h1>
          <p className="text-wt-text-muted mt-1">Connect email accounts to send and receive emails in your Inbox</p>
        </div>
        <Badge className={accounts.length > 0 ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}>
          {accounts.length > 0 ? <><CheckCircle2 className="h-3 w-3 mr-1" />{accounts.length} Account{accounts.length > 1 ? 's' : ''} Connected</> : <><XCircle className="h-3 w-3 mr-1" />Not Connected</>}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="accounts" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
            <Server className="h-4 w-4 mr-2" />IMAP/SMTP
          </TabsTrigger>
          <TabsTrigger value="gmail" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
            <Mail className="h-4 w-4 mr-2" />Gmail
          </TabsTrigger>
          <TabsTrigger value="domain" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
            <Shield className="h-4 w-4 mr-2" />Domain Auth
          </TabsTrigger>
          <TabsTrigger value="forwarding" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <ArrowRight className="h-4 w-4 mr-2" />Forwarding
          </TabsTrigger>
        </TabsList>

        {/* ── IMAP/SMTP Tab ── */}
        <TabsContent value="accounts" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-wt-text-muted">Connect any email provider using IMAP/SMTP credentials.</p>
            <Button onClick={() => setAddDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="add-email-account-btn">
              <Plus className="h-4 w-4 mr-2" />Add Email Account
            </Button>
          </div>

          {accounts.filter(a => (a.provider_type || 'imap_smtp') === 'imap_smtp').length === 0 ? (
            <Card className="bg-wt-surface border-wt-border">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Mail className="h-12 w-12 text-wt-text-muted mb-4" />
                <p className="text-wt-text-muted text-lg mb-2">No email accounts connected</p>
                <p className="text-wt-text-muted text-sm mb-4">Add your email account to start receiving emails in your Inbox</p>
                <Button onClick={() => setAddDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                  <Plus className="h-4 w-4 mr-2" />Connect Email Account
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Filter out Gmail OAuth accounts — they have their own
                  dedicated tab. The /email/accounts endpoint returns
                  every row in `email_accounts` (including the docs
                  the Gmail OAuth callback inserts with
                  `provider_type: "gmail"`), which is why the same
                  account previously appeared in both tabs. */}
              {accounts
                .filter(a => (a.provider_type || 'imap_smtp') === 'imap_smtp')
                .map(account => (
                <motion.div
                  key={account.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-wt-surface border border-wt-border rounded-xl p-4"
                  data-testid={`email-account-${account.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Mail className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-wt-text-primary">{account.account_name}</p>
                        <p className="text-sm text-wt-text-muted">{account.email_address}</p>
                        <p className="text-xs text-wt-text-muted">
                          {account.imap_host}:{account.imap_port} | {account.smtp_host}:{account.smtp_port}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={account.connection_status === 'connected' ? 'bg-green-500/20 text-green-400' : account.sync_error ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}>
                        {account.connection_status === 'connected' ? 'Connected' : account.sync_error ? 'Error' : 'Pending'}
                      </Badge>
                      {account.last_sync_at && (
                        <span className="text-xs text-wt-text-muted">
                          Last sync: {new Date(account.last_sync_at).toLocaleString()}
                        </span>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleSyncAccount(account.id)} title="Sync now">
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleTestExisting(account.id)} title="Test connection">
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteAccount(account.id)} className="text-red-400 hover:text-red-300" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {account.sync_error && (
                    <Alert className="mt-2 bg-red-500/10 border-red-500/30">
                      <AlertCircle className="h-4 w-4 text-red-400" />
                      <AlertDescription className="text-red-400 text-sm">{account.sync_error}</AlertDescription>
                    </Alert>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Gmail Tab ── */}
        <TabsContent value="gmail" className="space-y-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-wt-text-primary">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Mail className="h-5 w-5 text-red-400" />
                </div>
                Gmail / Google Workspace
              </CardTitle>
              <CardDescription>Connect Gmail accounts with one-click OAuth authentication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {gmailStatus?.oauth_configured ? (
                <>
                  {/* Connected Gmail Accounts */}
                  {gmailStatus.accounts?.length > 0 ? (
                    <div className="space-y-3">
                      {gmailStatus.accounts.map(acc => (
                        <div key={acc.id} className="flex items-center justify-between bg-wt-paper p-4 rounded-lg border border-wt-border" data-testid={`gmail-account-${acc.id}`}>
                          <div className="flex items-center gap-3">
                            {acc.gmail_user_info?.picture ? (
                              <img src={acc.gmail_user_info.picture} alt="" className="h-10 w-10 rounded-full" />
                            ) : (
                              <div className="p-2 bg-red-500/20 rounded-full">
                                <Mail className="h-5 w-5 text-red-400" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-wt-text-primary">{acc.gmail_user_info?.name || acc.account_name}</p>
                              <p className="text-sm text-wt-text-muted">{acc.email_address}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {acc.needs_reconnect || acc.sync_error ? (
                              <Badge className="bg-amber-500/20 text-amber-400" data-testid={`gmail-sync-issue-${acc.id}`} title={acc.sync_error || ''}>
                                <XCircle className="h-3 w-3 mr-1" />
                                {acc.needs_reconnect ? 'Reconnect needed' : 'Sync error'}
                              </Badge>
                            ) : (
                              <Badge className="bg-green-500/20 text-green-400">
                                <CheckCircle2 className="h-3 w-3 mr-1" />Connected
                              </Badge>
                            )}
                            {acc.last_sync_at && (
                              <span className="text-xs text-wt-text-muted hidden sm:inline">
                                Synced {new Date(acc.last_sync_at).toLocaleString()}
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleBackfillGmail(acc.id)}
                              title="Re-sync the last 30 days of emails"
                              data-testid={`gmail-backfill-${acc.id}`}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />Sync 30d
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDisconnectGmail(acc.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="h-4 w-4 mr-1" />Disconnect
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Connect Button */}
                  <Button
                    onClick={handleConnectGmail}
                    disabled={connectingGmail}
                    className="w-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 h-12"
                    data-testid="connect-gmail-btn"
                  >
                    {connectingGmail ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    {connectingGmail ? 'Connecting...' : 'Connect Gmail Account'}
                  </Button>

                  <p className="text-xs text-wt-text-muted text-center">
                    You will be redirected to Google to authorize access to your Gmail.
                  </p>
                </>
              ) : (
                <Alert className="bg-yellow-500/10 border-yellow-500/30">
                  <AlertCircle className="h-4 w-4 text-yellow-400" />
                  <AlertDescription className="text-yellow-400">
                    Gmail OAuth is not configured. Contact your administrator.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Domain Auth Tab ── */}
        <TabsContent value="domain" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-wt-text-muted">Configure SPF, DKIM, and DMARC for your sending domain to improve deliverability.</p>
            </div>
            <Button onClick={() => setDomainDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="add-domain-btn">
              <Plus className="h-4 w-4 mr-2" />Add Domain
            </Button>
          </div>

          {domains.length === 0 ? (
            <Card className="bg-wt-surface border-wt-border">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Shield className="h-12 w-12 text-wt-text-muted mb-4" />
                <p className="text-wt-text-muted text-lg mb-2">No domains configured</p>
                <p className="text-wt-text-muted text-sm mb-4">Add your domain to set up email authentication (SPF, DKIM, DMARC)</p>
                <Button onClick={() => setDomainDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                  <Plus className="h-4 w-4 mr-2" />Add Domain
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {domains.map(domain => (
                <Card key={domain.id} className="bg-wt-surface border-wt-border" data-testid={`domain-${domain.id}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-wt-text-primary text-lg">
                        <Globe className="h-5 w-5 text-blue-400" />
                        {domain.domain}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={domain.status === 'verified' ? 'bg-green-500/20 text-green-400' : domain.status === 'partially_verified' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {domain.status === 'verified' ? 'Verified' : domain.status === 'partially_verified' ? 'Partially Verified' : 'Pending Verification'}
                        </Badge>
                        <Button variant="outline" size="sm" onClick={() => handleVerifyDomain(domain.id)} className="border-wt-border text-wt-text-primary text-xs">
                          <RefreshCw className="h-3 w-3 mr-1" />Check Status
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDomain(domain.id)} className="text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-wt-text-muted">Add the following DNS records to your domain's DNS settings:</p>

                    {/* SES sandbox banner — AWS starts every account in
                        sandbox mode (max 200 emails/day, recipients must be
                        pre-verified). Broadcasts/journeys silently break
                        in this state, so we surface it prominently with a
                        link to the AWS production-access request form. */}
                    {domain.ses_sandbox?.sandbox && (
                      <div
                        className="p-3 rounded-md border border-yellow-500/40 bg-yellow-500/10"
                        data-testid={`ses-sandbox-${domain.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-xs">
                            <p className="font-semibold text-yellow-300 mb-1">
                              SES is in sandbox mode — request production access
                            </p>
                            <p className="text-wt-text-muted leading-relaxed mb-2">
                              Your AWS SES account can only send to <strong>verified addresses</strong> right now,
                              capped at <strong>{domain.ses_sandbox.max_24hr_send}/day</strong>. Broadcasts and
                              journey emails to real customers will silently fail until you request production access from AWS.
                            </p>
                            <a
                              href="https://console.aws.amazon.com/support/home#/case/create?issueType=service-limit-increase&limitType=service-code-ses"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-yellow-300 hover:text-yellow-200 underline"
                              data-testid={`ses-request-production-${domain.id}`}
                            >
                              Request production access →
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    {domain.ses_sandbox && !domain.ses_sandbox.sandbox && (
                      <div className="flex items-center gap-2 text-xs text-green-400">
                        <CheckCircle2 className="h-3 w-3" />
                        <span>
                          SES production access granted — {domain.ses_sandbox.max_24hr_send.toLocaleString()} emails/day,
                          {' '}{domain.ses_sandbox.max_send_rate}/s rate. Used today: {domain.ses_sandbox.sent_last_24hr.toLocaleString()}.
                        </span>
                      </div>
                    )}

                    {/* Sending domain info */}
                    {domain.sending_subdomain && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-wt-text-muted">Sending domain:</span>
                        <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary font-mono">{domain.sending_domain}</code>
                      </div>
                    )}
                    {domain.reply_to_email && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-wt-text-muted">Reply-to:</span>
                        <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary">{domain.reply_to_email}</code>
                      </div>
                    )}

                    {/* SES Verification TXT Record */}
                    {domain.dns_records?.verification && (
                      <div className="border border-blue-500/30 bg-blue-500/5 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm text-blue-400">SES Domain Verification</span>
                          <Badge className={domain.record_statuses?.ses_verification === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {domain.record_statuses?.ses_verification === 'active' ? 'Active' : 'Pending'}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between"><span className="text-wt-text-muted">Type:</span><span className="text-wt-text-primary">TXT</span></div>
                          <div className="flex items-center gap-2">
                            <span className="text-wt-text-muted">Name:</span>
                            <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary">{domain.dns_records.verification.name}</code>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(domain.dns_records.verification.name)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-wt-text-muted">Value:</span>
                            <code className="bg-wt-paper px-2 py-1 rounded text-xs text-wt-text-primary flex-1 truncate">{domain.dns_records.verification.value}</code>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(domain.dns_records.verification.value)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SPF */}
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-wt-text-primary">SPF Record</span>
                        <Badge className={domain.record_statuses?.spf === 'conflict' ? 'bg-red-500/20 text-red-400' : (domain.dns_records?.spf?.verified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400')}>
                          {domain.record_statuses?.spf === 'conflict' ? 'Conflict' : (domain.dns_records?.spf?.verified ? 'Active' : 'Pending')}
                        </Badge>
                      </div>

                      {/* Multi-SPF detector — surfaced when the apex
                          domain has more than one v=spf1 TXT record.
                          Receivers treat that as PermError per RFC 7208
                          and dump the mail to spam, which is exactly
                          what merchants hit when they keep their old
                          provider SPF (e.g. GoDaddy `secureserver.net`)
                          alongside our SES `amazonses.com` SPF. */}
                      {domain.spf_warning && (
                        <div
                          className="mb-3 p-3 rounded-md border border-red-500/40 bg-red-500/10"
                          data-testid={`spf-conflict-${domain.id}`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 text-xs text-red-300">
                              <p className="font-semibold mb-1">
                                {domain.spf_warning.count} SPF records detected — must be merged into one
                              </p>
                              <p className="text-wt-text-muted leading-relaxed">
                                {domain.spf_warning.explanation}
                              </p>
                            </div>
                          </div>
                          <div className="text-xs text-wt-text-muted mb-1">Currently published:</div>
                          <ul className="space-y-1 mb-3">
                            {(domain.spf_warning.current_records || []).map((rec, i) => (
                              <li key={i}>
                                <code className="block bg-wt-paper px-2 py-1 rounded text-[11px] text-wt-text-primary break-all">
                                  {rec}
                                </code>
                              </li>
                            ))}
                          </ul>
                          <div className="text-xs text-wt-text-muted mb-1">Replace ALL of the above with this single record:</div>
                          <div className="flex items-center gap-2">
                            <code className="bg-wt-paper px-2 py-1 rounded text-xs text-green-300 flex-1 break-all">
                              {domain.spf_warning.suggested_record}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 flex-shrink-0"
                              onClick={() => copyToClipboard(domain.spf_warning.suggested_record)}
                              data-testid={`spf-suggested-copy-${domain.id}`}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-wt-text-muted">Type:</span><span className="text-wt-text-primary">{domain.dns_records?.spf?.type}</span></div>
                        <div className="flex justify-between"><span className="text-wt-text-muted">Name:</span><span className="text-wt-text-primary">{domain.dns_records?.spf?.name}</span></div>
                        <div className="flex items-center gap-2">
                          <span className="text-wt-text-muted">Value:</span>
                          <code className="bg-wt-paper px-2 py-1 rounded text-xs text-wt-text-primary flex-1 truncate">{domain.dns_records?.spf?.value}</code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(domain.dns_records?.spf?.value)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* DKIM */}
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-wt-text-primary">DKIM Records (3 CNAME entries)</span>
                      </div>
                      {domain.dns_records?.dkim?.records?.map((rec, idx) => (
                        <div key={idx} className="space-y-1 text-xs mb-2 pb-2 border-b border-wt-border/50 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="text-wt-text-muted">Name:</span>
                            <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary flex-1 truncate">{rec.name}</code>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(rec.name)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-wt-text-muted">Value:</span>
                            <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary flex-1 truncate">{rec.value}</code>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(rec.value)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* DMARC */}
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-wt-text-primary">DMARC Record</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-wt-text-muted">Type:</span><span className="text-wt-text-primary">{domain.dns_records?.dmarc?.type}</span></div>
                        <div className="flex items-center gap-2">
                          <span className="text-wt-text-muted">Name:</span>
                          <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary">{domain.dns_records?.dmarc?.name}</code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(domain.dns_records?.dmarc?.name)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-wt-text-muted">Value:</span>
                          <code className="bg-wt-paper px-2 py-0.5 rounded text-xs text-wt-text-primary flex-1 truncate">{domain.dns_records?.dmarc?.value}</code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(domain.dns_records?.dmarc?.value)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* MX Record */}
                    <div className="border border-wt-border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-wt-text-primary">MX Record (for receiving via SES)</span>
                        <Badge className="bg-gray-500/20 text-gray-400">Optional</Badge>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-wt-text-muted">Type:</span><span className="text-wt-text-primary">MX</span></div>
                        <div className="flex justify-between"><span className="text-wt-text-muted">Priority:</span><span className="text-wt-text-primary">{domain.dns_records?.mx?.priority}</span></div>
                        <div className="flex items-center gap-2">
                          <span className="text-wt-text-muted">Value:</span>
                          <code className="bg-wt-paper px-2 py-0.5 rounded text-wt-text-primary">{domain.dns_records?.mx?.value}</code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(domain.dns_records?.mx?.value)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-wt-text-muted italic">{domain.dns_records?.mx?.note}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Forwarding Tab ── */}
        <TabsContent value="forwarding" className="space-y-4">
          <Card className="bg-wt-surface border-wt-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-wt-text-primary">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <ArrowRight className="h-5 w-5 text-purple-400" />
                </div>
                Email Forwarding Address
              </CardTitle>
              <CardDescription>
                Auto-forward emails from your email provider to this address. They will appear in your Inbox.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {forwardingAddress ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 bg-wt-paper p-4 rounded-lg border border-wt-border">
                    <Mail className="h-6 w-6 text-purple-400 flex-shrink-0" />
                    <code className="text-lg font-mono text-wt-text-primary flex-1">{forwardingAddress.forwarding_address}</code>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(forwardingAddress.forwarding_address)}>
                      <Copy className="h-4 w-4 mr-2" />Copy
                    </Button>
                  </div>

                  <div className="space-y-3 text-sm text-wt-text-muted">
                    <p className="font-medium text-wt-text-primary">How to set up forwarding:</p>
                    <div className="space-y-2">
                      <div className="flex gap-3">
                        <span className="bg-wt-gold/20 text-wt-gold rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>Open your email provider's settings (Gmail, Outlook, etc.)</span>
                      </div>
                      <div className="flex gap-3">
                        <span className="bg-wt-gold/20 text-wt-gold rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>Find "Forwarding" or "Auto-forward" settings</span>
                      </div>
                      <div className="flex gap-3">
                        <span className="bg-wt-gold/20 text-wt-gold rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>Add the forwarding address above and verify it</span>
                      </div>
                      <div className="flex gap-3">
                        <span className="bg-wt-gold/20 text-wt-gold rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>All forwarded emails will appear in your Opswarex Inbox</span>
                      </div>
                    </div>
                  </div>

                  <Alert className="bg-blue-500/10 border-blue-500/30">
                    <AlertCircle className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-blue-400 text-sm">
                      Forwarding requires Amazon SES domain configuration. Connect via IMAP/SMTP tab for immediate access to your emails.
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-wt-text-muted" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Add Email Account Dialog ── */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Connect Email Account</DialogTitle>
            <DialogDescription>Add an email account using IMAP/SMTP</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Provider Selection */}
            {!selectedProvider && (
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Select Email Provider</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_PROVIDERS.map(provider => (
                    <Button
                      key={provider.name}
                      variant="outline"
                      className="h-auto py-3 px-4 justify-start border-wt-border hover:border-wt-gold/50 text-wt-text-primary"
                      onClick={() => selectProvider(provider)}
                      data-testid={`provider-${provider.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <Mail className="h-4 w-4 mr-2 text-blue-400" />
                      {provider.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {selectedProvider && (
              <>
                <div className="flex items-center justify-between">
                  <Badge className="bg-blue-500/20 text-blue-400">{selectedProvider.name}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedProvider(null)} className="text-wt-text-muted text-xs">
                    Change Provider
                  </Button>
                </div>

                {selectedProvider.note && (
                  <Alert className="bg-blue-500/10 border-blue-500/30">
                    <AlertCircle className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-blue-400 text-sm">{selectedProvider.note}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-3">
                  <div>
                    <Label className="text-wt-text-primary">Account Name</Label>
                    <Input placeholder="e.g. Support Email" value={formData.account_name} onChange={e => setFormData(p => ({...p, account_name: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" data-testid="account-name-input" />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">Email Address</Label>
                    <Input placeholder="support@yourcompany.com" value={formData.email_address} onChange={e => setFormData(p => ({...p, email_address: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" data-testid="email-address-input" />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">Username (if different from email)</Label>
                    <Input placeholder="Leave blank to use email address" value={formData.username} onChange={e => setFormData(p => ({...p, username: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" />
                  </div>
                  <div>
                    <Label className="text-wt-text-primary">Password / App Password</Label>
                    <div className="relative">
                      <Input type={showPassword ? 'text' : 'password'} placeholder="Enter password or app password" value={formData.password} onChange={e => setFormData(p => ({...p, password: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary pr-10" data-testid="password-input" />
                      <Button variant="ghost" size="icon" className="absolute right-0 top-0 h-full" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Server Details (editable for Custom) */}
                  {selectedProvider.name === 'Custom' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-wt-text-primary">IMAP Host</Label>
                          <Input placeholder="imap.example.com" value={formData.imap_host} onChange={e => setFormData(p => ({...p, imap_host: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" />
                        </div>
                        <div>
                          <Label className="text-wt-text-primary">IMAP Port</Label>
                          <Input type="number" value={formData.imap_port} onChange={e => setFormData(p => ({...p, imap_port: parseInt(e.target.value) || 993}))} className="bg-wt-paper border-wt-border text-wt-text-primary" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-wt-text-primary">SMTP Host</Label>
                          <Input placeholder="smtp.example.com" value={formData.smtp_host} onChange={e => setFormData(p => ({...p, smtp_host: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" />
                        </div>
                        <div>
                          <Label className="text-wt-text-primary">SMTP Port</Label>
                          <Input type="number" value={formData.smtp_port} onChange={e => setFormData(p => ({...p, smtp_port: parseInt(e.target.value) || 587}))} className="bg-wt-paper border-wt-border text-wt-text-primary" />
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Switch checked={formData.use_ssl} onCheckedChange={v => setFormData(p => ({...p, use_ssl: v}))} />
                          <Label className="text-wt-text-muted text-sm">SSL</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch checked={formData.use_tls} onCheckedChange={v => setFormData(p => ({...p, use_tls: v}))} />
                          <Label className="text-wt-text-muted text-sm">TLS</Label>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedProvider.name !== 'Custom' && (
                    <div className="bg-wt-paper rounded-lg p-3 text-xs text-wt-text-muted space-y-1">
                      <p><strong>IMAP:</strong> {formData.imap_host}:{formData.imap_port} (SSL: {formData.use_ssl ? 'Yes' : 'No'})</p>
                      <p><strong>SMTP:</strong> {formData.smtp_host}:{formData.smtp_port} (TLS: {formData.use_tls ? 'Yes' : 'No'})</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {selectedProvider && (
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleTestConnection} disabled={testing} className="border-wt-border text-wt-text-primary" data-testid="test-connection-btn">
                {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Test Connection
              </Button>
              <Button onClick={handleSaveAccount} disabled={saving} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="save-account-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Save Account
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add Domain Dialog ── */}
      <Dialog open={domainDialogOpen} onOpenChange={setDomainDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Add Domain</DialogTitle>
            <DialogDescription>Enter your sending domain to set up email authentication</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-wt-text-primary">Domain</Label>
              <Input placeholder="yourcompany.com" value={domainForm.domain} onChange={e => setDomainForm(p => ({...p, domain: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" data-testid="domain-input" />
            </div>
            <div>
              <Label className="text-wt-text-primary">Sending Subdomain (optional)</Label>
              <div className="flex items-center gap-2">
                <Input placeholder="e.g. mail" value={domainForm.sending_subdomain} onChange={e => setDomainForm(p => ({...p, sending_subdomain: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary w-32" />
                <span className="text-wt-text-muted text-sm">.{domainForm.domain || 'yourcompany.com'}</span>
              </div>
              <p className="text-xs text-wt-text-muted mt-1">Recommended: use a subdomain like "mail" or "update" to protect your main domain reputation</p>
            </div>
            <div>
              <Label className="text-wt-text-primary">Reply-to Email Address</Label>
              <Input placeholder="support@yourcompany.com" value={domainForm.reply_to_email} onChange={e => setDomainForm(p => ({...p, reply_to_email: e.target.value}))} className="bg-wt-paper border-wt-border text-wt-text-primary" />
              <p className="text-xs text-wt-text-muted mt-1">A verification email will be sent to this address</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDomainDialogOpen(false)} className="border-wt-border text-wt-text-primary">Cancel</Button>
            <Button onClick={handleAddDomain} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="save-domain-btn">
              <Plus className="h-4 w-4 mr-2" />Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailIntegrationPage;
